import React from "react";
import { motion } from "framer-motion";

const easing = [0.22, 1, 0.36, 1];
// Start near-transparent rather than fully transparent: the browser then counts the element
// as painted on first render, so hero text registers as LCP immediately instead of after the animation.
const PAINTED = 0.02;

// True on the server and during the first client render that hydrates prerendered HTML.
// In both cases elements render in their final state so markup matches and nothing flashes.
const skipIntro = () => typeof window === "undefined" || window.__pmnPrerendered === true;

// Scroll-triggered reveal
export function Reveal({ children, delay = 0, y = 28, className = "", once = true, as = "div", ...rest }) {
  const Tag = motion[as];
  return (
    <Tag
      {...rest}
      className={className}
      initial={skipIntro() ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: easing }}
    >
      {children}
    </Tag>
  );
}

// Staggered line-by-line rise for kinetic headings
// `instant` renders the final state with no entrance animation (used when the static HTML shell already painted it).
export function KineticHeading({ lines, className = "", delay = 0, stagger = 0.1, instant = false }) {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            style={{ display: "block" }}
            initial={instant || skipIntro() ? false : { y: "40%", opacity: PAINTED }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 0.85, delay: delay + i * stagger, ease: easing }}
          >
            {line}
          </motion.span>
          {i < lines.length - 1 ? " " : null}
        </span>
      ))}
    </span>
  );
}

// Simple fade for on-load elements
export function FadeIn({ children, delay = 0, className = "", instant = false }) {
  return (
    <motion.div
      className={className}
      initial={instant || skipIntro() ? false : { opacity: PAINTED, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: easing }}
    >
      {children}
    </motion.div>
  );
}
